import SkapaElement from '@ingka/skapa-webc-element';
import { generateDangerIcon } from './DangerIcon.js';
import { generateLoadingBall } from './LoadingBall.js';
const defaultSize = 'medium';
const buttonPartsInitializer = { iconSlot: undefined, gap: undefined, label: undefined, loading: undefined };
export class ButtonAbstraction extends SkapaElement {
    constructor() {
        super();
        this.parts = { ...buttonPartsInitializer };
        this.hasIcon = false;
        this.__loadingAriaLabel = 'loading';
        this.disableClick = (ev) => {
            if (this.loading || this.disabled) {
                ev.preventDefault();
                ev.stopPropagation();
            }
        };
        this.handleSlotChange = (ev) => {
            if (ev.target && (ev.target instanceof HTMLSlotElement)) {
                if (ev.target.name === 'icon') {
                    this.inspectIconState();
                }
            }
        };
        this.handleFormControl = () => {
            if (!this.type || this.loading || this.disabled)
                return;
            const form = this.closest('form');
            if (form && this.type) {
                if (this.type === 'reset')
                    return form.reset();
                else if (this.type === 'submit') {
                    const event = new SubmitEvent('submit', { submitter: this, cancelable: true });
                    form.reportValidity();
                    form.dispatchEvent(event);
                    setTimeout(() => {
                        if (event.defaultPrevented)
                            return;
                        if (form.checkValidity())
                            form.submit();
                    });
                }
            }
        };
        this.syncDecorative = () => {
            var _a;
            if ((_a = this.spanBase) === null || _a === void 0 ? void 0 : _a.parentNode) {
                this.spanBase.id = 'btn';
            }
        };
        this.syncAnchor = () => {
            var _a;
            if ((_a = this.anchorBase) === null || _a === void 0 ? void 0 : _a.parentNode) {
                this.anchorBase.id = 'btn';
                this.syncAriaStates();
                if (!this.disabled && this.href)
                    this.anchorBase.setAttribute('href', this.href);
                if (this.newWindow)
                    this.anchorBase.setAttribute('target', '_blank');
                else
                    this.anchorBase.removeAttribute('target');
                this.anchorBase.addEventListener('keyup', this._onKeyUpHandler);
            }
        };
        this.syncButton = () => {
            if (this.buttonBase.parentNode) {
                this.syncAriaStates();
                this.buttonBase.disabled = this.disabled;
                this.buttonBase.addEventListener('click', this.handleFormControl);
            }
        };
        this.syncAriaStates = () => {
            if (this.a11yLabel)
                this.currentBase.setAttribute('aria-label', this.a11yLabel);
            else
                this.currentBase.removeAttribute('aria-label');
            this.currentBase.setAttribute('aria-disabled', (this.loading || this.disabled).toString());
        };
        this.syncState = () => {
            if (this.href) {
                // Use anchor base
                if (!this.anchorBase)
                    this.anchorBase = document.createElement('a');
                if (!this.anchorBase.parentNode) {
                    // If the root element is not the right type
                    this.switchBaseTo(this.anchorBase);
                }
                // Synchronize the properties
                this.syncAnchor();
            }
            else if (this.decorativeOnly) {
                // Use a span base
                if (!this.spanBase)
                    this.spanBase = document.createElement('span');
                if (!this.spanBase.parentNode) {
                    // If the root element is not the right type
                    this.switchBaseTo(this.spanBase);
                }
                // Synchronize the properties
                this.syncDecorative();
            }
            else {
                if (!this.buttonBase.parentNode) {
                    // If the root element is not the right type
                    this.switchBaseTo(this.buttonBase);
                }
                // Synchronize the properties
                this.syncButton();
            }
            // Update the content
            const root = this.currentBase;
            const innerContent = this.innerContent; // Elements to be rendered
            // first let's remove all elements that shouldn't be there
            Array.from(root.children).forEach(element => !innerContent.includes(element) && element.remove());
            // Only touch those elements that are missing or not in order.
            innerContent.forEach((element, index) => {
                if (root.children.item(index) !== element) {
                    // order issue or element is not added
                    if (index === 0)
                        root.prepend(element);
                    else
                        innerContent[index - 1].after(element);
                }
            });
        };
        this.gatherProperties(['disabled', 'href', 'inverse', 'loading', 'size', 'newWindow', 'decorativeOnly', 'variant', 'a11yLabel', 'type'], this);
        const shadow = this.attachShadow({ mode: 'open', delegatesFocus: true });
        this.currentBase = this.buttonBase = document.createElement('button');
        this.currentBase.id = 'btn';
        this.currentBase.append(...this.innerContent);
        shadow.append(this.buttonBase);
    }
    get disabled() {
        return this.hasAttribute('disabled');
    }
    set disabled(toggle) {
        this.toggleAttribute('disabled', !!toggle);
    }
    get href() {
        return this.getAttribute('href');
    }
    set href(url) {
        if (url)
            this.setAttribute('href', url);
        else
            this.removeAttribute('href');
    }
    get inverse() {
        return this.hasAttribute('inverse');
    }
    set inverse(toggle) {
        this.toggleAttribute('inverse', !!toggle);
    }
    get loading() {
        return this.hasAttribute('loading');
    }
    set loading(toggle) {
        this.toggleAttribute('loading', !!toggle);
    }
    get size() {
        return this.getAttribute('size') || defaultSize;
    }
    set size(size) {
        if (size === defaultSize) {
            this.removeAttribute('size');
        }
        else {
            this.setAttribute('size', size);
        }
    }
    get newWindow() {
        return this.hasAttribute('new-window');
    }
    set newWindow(toggle) {
        this.toggleAttribute('new-window', !!toggle);
    }
    get decorativeOnly() {
        return this.hasAttribute('decorative-only');
    }
    set decorativeOnly(toggle) {
        this.toggleAttribute('decorative-only', !!toggle);
    }
    get variant() {
        return this.getAttribute('variant') || 'primary';
    }
    set variant(variant) {
        if (variant)
            this.setAttribute('variant', variant);
        else
            this.removeAttribute('variant');
    }
    get a11yLabel() {
        return this.getAttribute('a11y-label') || '';
    }
    set a11yLabel(label) {
        if (label)
            this.setAttribute('a11y-label', label);
        else
            this.removeAttribute('a11y-label');
    }
    get type() {
        return this.getAttribute('type');
    }
    set type(type) {
        if (type)
            this.setAttribute('type', type);
        else
            this.removeAttribute('type');
    }
    get loadingSrText() {
        return this.__loadingAriaLabel || '';
    }
    set loadingSrText(ariaLabel) {
        if (this.loading && this.parts.loading) {
            // update existing loading
            this.buttonBase.setAttribute('aria-label', ariaLabel || '');
        }
        this.__loadingAriaLabel = ariaLabel;
    }
    get control() {
        return this.currentBase;
    }
    connectedCallback() {
        super.connectedCallback();
        this.inspectIconState();
        this.syncState();
        this.shadowRoot.addEventListener('slotchange', this.handleSlotChange);
        this.addEventListener('click', this.disableClick, { capture: true });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.shadowRoot.removeEventListener('slotchange', this.handleSlotChange);
        this.removeEventListener('click', this.disableClick, { capture: true });
        if (this.anchorBase)
            this.anchorBase.removeEventListener('keyup', this._onKeyUpHandler);
        this.buttonBase.removeEventListener('click', this.handleFormControl);
    }
    inspectIconState() {
        if (this.parts.iconSlot && this.parts.iconSlot.assignedElements().length > 0 !== this.hasIcon) {
            // Icon state changed
            this.hasIcon = this.parts.iconSlot.assignedElements().length > 0;
            this.micro(this.syncState, 'synchronize wrapper');
        }
    }
    // role="button" space bar activation polyfill. I'm not sure if the browsers don't support this by default by now.
    _onKeyUpHandler(e) {
        // Return early if not spacebar, e.g. 32.
        if (this.disabled || this.loading || e.key !== ' ')
            return;
        e.target.click();
    }
    get iconFragment() {
        var _a;
        if (!this.parts.iconSlot) {
            const slot = this.parts.iconSlot = document.createElement('slot');
            slot.id = 'icon';
            slot.name = 'icon';
        }
        if (this.variant === 'danger') {
            if (!this.dangerIcon)
                this.dangerIcon = generateDangerIcon();
            this.parts.iconSlot.append(this.dangerIcon);
        }
        else if ((_a = this.dangerIcon) === null || _a === void 0 ? void 0 : _a.parentNode) {
            this.dangerIcon.remove();
        }
        return this.parts.iconSlot;
    }
    get gapFragment() {
        if ((this.hasIcon || this.variant === 'danger')) {
            if (!this.parts.gap) {
                const gap = this.parts.gap = document.createElement('span');
                gap.id = 'gap';
            }
            return this.parts.gap;
        }
    }
    get labelFragment() {
        if (!this.parts.label) {
            const label = this.parts.label = document.createElement('slot');
            label.id = 'label';
        }
        return this.parts.label;
    }
    get loadingFragment() {
        if (this.loading) {
            // Initialise loading fragment if does not yet exist.
            if (!this.parts.loading)
                this.parts.loading = generateLoadingBall();
            this.buttonBase.setAttribute('aria-label', this.loadingSrText);
            return this.parts.loading;
        }
    }
    get innerContent() {
        return [this.iconFragment, this.gapFragment, this.labelFragment, this.loadingFragment].filter(Boolean);
    }
    attributeChangedCallback(name, oldVal, newVal) {
        this.micro(this.syncState, 'synchronize wrapper'); // Microtask the update to prevent intensive initialisations.
    }
    switchBaseTo(base) {
        // restructure the shadowdom.
        this.currentBase.remove();
        this.shadowRoot.append(base);
        this.currentBase = base;
    }
}
ButtonAbstraction.observedAttributes = ['disabled', 'href', 'loading', 'size', 'new-window', 'decorative-only', 'type', 'a11y-label'];
export default ButtonAbstraction;
